import * as types from './sortingModel.actionTypes';
import { queryClient } from '../../providers/QueryProvider';
import {
  createSortingModel as createSortingModelRequest,
  getSortingResultsByUrl,
  clearHospitalCache,
  getSortingCacheInfo,
} from '../../services/apiService';

const baseUrl = process.env.REACT_APP_BASE_URL;

// Create sorting model with weightages
export const createSortingModel = (weightages, page = 1, perPage = 100) => async (dispatch) => {
  dispatch({ type: types.CREATE_SORTING_MODEL_REQUEST });

  try {
    const data = await createSortingModelRequest({
      weightages,
      page,
      per_page: perPage,
    });

    if (data.success) {
      dispatch({
        type: types.CREATE_SORTING_MODEL_SUCCESS,
        payload: data
      });
      // The ranking basis changed, so any cached ranked reads are now stale.
      queryClient.invalidateQueries({ queryKey: ["sorting-results"] });
      return { success: true, data };
    } else {
      throw new Error(data.error || 'Failed to create sorting model');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || 'Failed to create sorting model';
    dispatch({
      type: types.CREATE_SORTING_MODEL_FAILURE,
      payload: errorMessage
    });
    return { success: false, error: errorMessage };
  }
};

export const fetchSortingResults =
  ({
    page = 1,
    perPage = 50,
    state,
    partnerExclusiveFlag = true,
    globalFilter = "all",
  } = {}) =>
  async (dispatch) => {
    dispatch({ type: types.FETCH_SORTING_RESULTS_REQUEST });
    // Sorting-model hospital list is isolated from app filters (hex, search,
    // advanced). Scope is only the local state dropdown: no state → /all;
    // with state → that state alone.
    const globalFilterValue =
      typeof globalFilter === "object" && globalFilter
        ? globalFilter.value
        : globalFilter;
    try {
      let url;
      if (state) {
        url = `${baseUrl}/new_data/hospitals/filter?states=${state}&partnerExclusiveArea=${partnerExclusiveFlag}&globalFilter=${globalFilterValue}&sort_by=ranking&sort_order=asc&page=${page}&per_page=${perPage}`;
      } else {
        url = `${baseUrl}/new_data/hospitals/all?sort_by=ranking&sort_order=asc&partnerExclusiveArea=${partnerExclusiveFlag}&globalFilter=${globalFilterValue}&page=${page}&per_page=${perPage}`;
      }

      // Cached per built URL (encodes filter + page), so re-selecting the same
      // sorting context is served from cache instead of refetching.
      const data = await queryClient.fetchQuery({
        queryKey: ["sorting-results", url],
        queryFn: ({ signal }) => getSortingResultsByUrl(url, signal),
        staleTime: 5 * 60 * 1000,
      });

      dispatch({
        type: types.FETCH_SORTING_RESULTS_SUCCESS,
        payload: data,
      });
      return { success: true, data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch sorting results";
      dispatch({
        type: types.FETCH_SORTING_RESULTS_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

export const loadMoreSortingResults =
  ({
    page,
    perPage = 50,
    state,
    partnerExclusiveFlag = true,
    globalFilter = "all",
  } = {}) =>
  async (dispatch) => {
    dispatch({ type: types.LOAD_MORE_SORTING_RESULTS_REQUEST });
    try {
      const globalFilterValue =
        typeof globalFilter === "object" && globalFilter
          ? globalFilter.value
          : globalFilter;
      let url;
      if (state) {
        url = `${baseUrl}/new_data/hospitals/filter?states=${state}&partnerExclusiveArea=${partnerExclusiveFlag}&globalFilter=${globalFilterValue}&sort_by=ranking&sort_order=asc&page=${page}&per_page=${perPage}`;
      } else {
        url = `${baseUrl}/new_data/hospitals/all?sort_by=ranking&sort_order=asc&partnerExclusiveArea=${partnerExclusiveFlag}&globalFilter=${globalFilterValue}&page=${page}&per_page=${perPage}`;
      }

      const data = await queryClient.fetchQuery({
        queryKey: ["sorting-results", url],
        queryFn: ({ signal }) => getSortingResultsByUrl(url, signal),
        staleTime: 5 * 60 * 1000,
      });

      dispatch({
        type: types.LOAD_MORE_SORTING_RESULTS_SUCCESS,
        payload: data,
      });
      return { success: true, data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to load more sorting results";
      dispatch({
        type: types.LOAD_MORE_SORTING_RESULTS_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

// Set weightages for sorting model
export const setSortingModelWeightages = (weightages) => ({
  type: types.SET_SORTING_MODEL_WEIGHTAGES,
  payload: weightages,
});

// Reset sorting model state
export const resetSortingModel = () => ({
  type: types.RESET_SORTING_MODEL,
});

// Set sorting model visibility
export const setSortingModelVisibility = (isVisible) => ({
  type: types.SET_SORTING_MODEL_VISIBILITY,
  payload: isVisible,
});

// Clear cache manually
export const clearHospitalDataCache = () => async (dispatch) => {
  try {
    const data = await clearHospitalCache();

    if (data.success) {
      // Server-side cache was cleared, so drop the cached reads that depend on
      // it and let them refetch on next use.
      queryClient.invalidateQueries({ queryKey: ["sorting-results"] });
      queryClient.invalidateQueries({ queryKey: ["hospitals"] });
      queryClient.invalidateQueries({ queryKey: ["top-25-hospitals"] });
      queryClient.invalidateQueries({ queryKey: ["card-filter-counts"] });
      return { success: true, message: data.message };
    } else {
      throw new Error(data.error || "Failed to clear cache");
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || error.message || "Failed to clear cache";
    console.error("Error clearing cache:", errorMessage);
    return { success: false, error: errorMessage };
  }
};

// Get cache information
export const getCacheInfo = () => async (dispatch) => {
  try {
    const data = await queryClient.fetchQuery({
      queryKey: ["sorting-cache-info"],
      queryFn: ({ signal }) => getSortingCacheInfo(signal),
      staleTime: 60 * 1000,
    });

    if (data.success) {
      return { success: true, info: data.info };
    } else {
      throw new Error(data.error || "Failed to get cache info");
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      "Failed to get cache info";
    console.error("Error getting cache info:", errorMessage);
    return { success: false, error: errorMessage };
  }
};
